import React, { useCallback, useMemo } from 'react';
import * as Clipboard from 'expo-clipboard';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View, ViewProps } from 'react-native';

import { Button, openNativeShare, ProductCardV2, ShareIcon, theme } from '@sg/garnish';
import { useDietaryRestrictions } from '@sg/shared-fe-features-menu';
import { useLineItemModifierIngredients } from '@sg/shared-fe-features-ordering';

import {
  useLineItemDescription,
  useLineItemIngredients,
  useLineItemName,
} from '@order/shared-hooks';
import { buildShareLineItemUrl } from '@order/utils';

import { useDietaryPreferencesMachine } from '../../context';
import { type PartialLineItem } from '../../graphql';
import { useReorderState } from '../../screens/ReorderingScreen/hooks';

export const SharedLineItem = (props: SharedLineItemProps) => {
  const {
    addToBagLabel,
    lineItem,
    onAddToBag,
    onCustomize,
    onFavorite,
    isAddingToBag,
    isAddToBagDisabled,
    productContainerStyle,
  } = props;

  const { id, slug, product, removedIngredients, addedIngredients, favorited = false } = lineItem;

  const { formatMessage } = useIntl();
  const { reordering, lineItemId } = useReorderState();

  // ─── Derived ─────────────────────────────────────────────────────────

  const shouldDisableAddToBag =
    isAddToBagDisabled ?? (reordering ? Number(id) !== Number(lineItemId) : undefined);
  const shouldShowAddToBagLoading =
    isAddingToBag ?? (reordering ? Number(id) === Number(lineItemId) : undefined);
  const addToBagPalette = onCustomize ? 'primary' : 'spinachMuted';

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleShare = useCallback(() => {
    if (!slug) return;

    const nativeText = formatMessage(messages.shareFavorite, { product_name: product.name });
    const url = buildShareLineItemUrl(slug);

    void openNativeShare({
      message: nativeText,
      url,
      options: { subject: nativeText },
    });
    void Clipboard.setStringAsync(url);
  }, [formatMessage, product.name, slug]);

  // ─────────────────────────────────────────────────────────────────────

  const productName = useLineItemName(product, lineItem);

  const lineItemIngredients = useLineItemIngredients({
    product,
    addedIngredients,
    removedIngredients,
  });
  const {
    addedIngredientsText: selectedAddedIngredientsText,
    removedIngredientsText: selectedRemovedIngredientsText,
  } = useLineItemModifierIngredients({
    selectedModifiers: lineItem.selectedModifiers,
    showCosts: false,
    showLabels: !product.isCustom,
    product,
  });
  const lineItemDescription = useLineItemDescription(lineItem);

  const { restrictions } = useDietaryPreferencesMachine({ referrer: 'Share' });
  const allergenProperties = product.allergenProperties;
  const getRestrictionNotice = useDietaryRestrictions({ restrictions });
  const restrictionNotice = getRestrictionNotice({ allergenProperties });

  const description = useMemo(() => {
    const selectedModifierDescription = [
      selectedAddedIngredientsText,
      selectedRemovedIngredientsText,
    ]
      .filter(Boolean)
      .join('\n');

    if (selectedModifierDescription) {
      return selectedModifierDescription;
    }

    const { addedIngredientsText, removedIngredientsText } = lineItemIngredients;

    const hasModifications =
      typeof addedIngredientsText === 'string' || typeof removedIngredientsText === 'string';

    if (!hasModifications) {
      return product?.description;
    }

    return [addedIngredientsText, removedIngredientsText].filter(Boolean).join('\n');
  }, [
    lineItemIngredients,
    product?.description,
    selectedAddedIngredientsText,
    selectedRemovedIngredientsText,
  ]);

  const favToggleAccessibilityLabel = favorited
    ? formatMessage(messages.removeFromFavorites, { product: productName })
    : formatMessage(messages.addToFavorites, { product: productName });

  // ─── Styles ──────────────────────────────────────────────────────────

  const actionButtonStyles = onCustomize ? styles.actionButton : undefined;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ProductCardV2.Container canHover={false} style={productContainerStyle}>
      {onFavorite ? (
        <ProductCardV2.FavoriteToggle
          isActive={favorited}
          onPress={onFavorite}
          accessibilityLabel={favToggleAccessibilityLabel}
        />
      ) : null}

      {product.asset?.url ? (
        <ProductCardV2.Image
          source={product.asset.url}
          alt={product.name}
          isUnavailable={product.outOfStock}
        />
      ) : null}

      <ProductCardV2.LabelContainer>
        {product.label?.name ? (
          <ProductCardV2.Label>{product.label.name}</ProductCardV2.Label>
        ) : null}
      </ProductCardV2.LabelContainer>

      <ProductCardV2.Heading>{productName}</ProductCardV2.Heading>

      {description ? <ProductCardV2.Description>{description}</ProductCardV2.Description> : null}

      {lineItemDescription ? (
        <ProductCardV2.Description>{lineItemDescription}</ProductCardV2.Description>
      ) : null}

      <ProductCardV2.Footer>
        <ProductCardV2.ProductDetailsContainer>
          {product.calories === undefined ? null : (
            <ProductCardV2.ProductDetail>
              <FormattedMessage
                {...messages.caloriesText}
                values={{ calories: product.calories ?? 0 }}
              />
            </ProductCardV2.ProductDetail>
          )}
        </ProductCardV2.ProductDetailsContainer>

        {restrictionNotice ? (
          <ProductCardV2.AllergenNotice>{restrictionNotice}</ProductCardV2.AllergenNotice>
        ) : null}
      </ProductCardV2.Footer>

      <ProductCardV2.ButtonsContainer>
        <View style={styles.actionsContainer}>
          {onAddToBag ? (
            <ProductCardV2.AddToBagButton
              disabled={shouldDisableAddToBag}
              isLoading={shouldShowAddToBagLoading}
              onPress={onAddToBag}
              palette={addToBagPalette}
              style={actionButtonStyles}
            >
              {addToBagLabel ?? <FormattedMessage {...messages.addToBag} />}
            </ProductCardV2.AddToBagButton>
          ) : null}

          {onCustomize ? (
            <Button
              palette="spinachMuted"
              size="large"
              onPress={onCustomize}
              style={actionButtonStyles}
            >
              <FormattedMessage {...messages.customize} />
            </Button>
          ) : null}
        </View>

        {slug ? <ShareIcon onShare={handleShare} /> : null}
      </ProductCardV2.ButtonsContainer>
    </ProductCardV2.Container>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  actionButton: {
    paddingHorizontal: theme.spacing['5'],
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  shareFavorite: {
    defaultMessage: 'Check out the {product_name} at sweetgreen!',
    description: 'Shared line item | Share favorite',
  },
  caloriesText: {
    defaultMessage: '{calories} cals',
    description: 'Shared line item | Product card | Calories',
  },
  addToFavorites: {
    defaultMessage: 'Add "{product}" to favorites',
    description: 'Shared line item | Add to favorites',
  },
  removeFromFavorites: {
    defaultMessage: 'Remove "{product}" from favorites',
    description: 'Shared line item | Remove from favorites',
  },
  addToBag: {
    defaultMessage: 'Add to bag',
    description: 'Shared line item | Add to bag',
  },
  customize: {
    defaultMessage: 'Customize',
    description: 'Shared line item | Customize',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type SharedLineItemProps = {
  lineItem: SharedLineItemLineItem;
  addToBagLabel?: string;
  productContainerStyle?: ViewProps['style'];
  isAddingToBag?: boolean;
  isAddToBagDisabled?: boolean;
  onAddToBag?: () => void;
  onCustomize?: () => void;
  onFavorite?: () => Promise<void>;
};

type SharedLineItemLineItem = Omit<PartialLineItem, 'selectedModifiers'> & {
  selectedModifiers?: Parameters<typeof useLineItemModifierIngredients>[0]['selectedModifiers'];
};
